/** Variant select event from the Horizon variant picker (StandardEvents.productSelect in @shopify/events). */
const PRODUCT_SELECT = 'shopify:product:select';
/** Same widths as the section's srcset. */
const WIDTHS = [800, 1200, 1600, 2000];

/**
 * @typedef {object} Row One variant, as the section writes it into its JSON script.
 * @property {number} id
 * @property {string[]} o Option values, by option position
 * @property {string} s Image URL
 * @property {number} w Image width
 * @property {number} r Image ratio, clamped like the section's
 * @property {number} x Print position across, %
 * @property {number} y Print position down, %
 * @property {string} a Image description
 */

/**
 * Maison · Calibre stage (sections/ms-calibre-stage.liquid).
 *
 * The pull-back and the captions are CSS on the engine's --p (assets/ms-motion.js). This element does two small
 * things: it marks itself data-live while the stage is moving, so the picture is promoted to its own layer only then,
 * and it follows the product page's variant picker. When a variant is selected it swaps the picture, the print
 * position and the clasp and colour words from the variant table the section wrote, with no request and no re-render.
 * Without this script the stage still plays, on the variant the page opened with.
 */
class MsCalibreStage extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {HTMLImageElement | null} */
  #img = null;
  /** @type {Map<string, Row>} */
  #rows = new Map();
  /** The image URL currently shown, to skip a swap between variants that share a picture. */
  #shown = '';
  #token = 0;

  connectedCallback() {
    this.#img = this.querySelector('.ms-cal__img');
    try {
      const table = JSON.parse(this.querySelector('[data-ms-cal-variants]')?.textContent || '[]');
      for (const row of table) this.#rows.set(String(row.id), row);
    } catch {
      return;
    }
    this.#shown = this.#rows.get(this.dataset.variantId ?? '')?.s ?? '';

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.addEventListener(
      'ms:progress',
      (event) => {
        const { p } = /** @type {CustomEvent} */ (event).detail;
        this.toggleAttribute('data-live', p > 0 && p < 1);
      },
      { signal }
    );
    document.addEventListener(PRODUCT_SELECT, this.#onSelect, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {Event} event */
  #onSelect = (event) => {
    // Cards and the quick-add dialog also dispatch it; only this page's picker counts.
    if (!(event.target instanceof Element) || event.target.closest('product-card, dialog')) return;
    const promise = /** @type {{ promise?: Promise<any> }} */ (event).promise;
    promise
      ?.then(({ detail }) => {
        if (!detail?.resource?.id || (detail.productId && detail.productId !== this.dataset.productId)) return;
        const row = this.#rows.get(String(detail.resource.id));
        if (row) this.#show(row);
      })
      .catch(() => {});
  };

  /** @param {Row} row */
  #show(row) {
    const token = ++this.#token;

    const clasp = this.querySelector('[data-ms-cal="clasp"]');
    const claspAt = Number(this.dataset.claspIndex);
    if (clasp && claspAt >= 0 && row.o[claspAt]) clasp.textContent = `${row.o[claspAt]} ${this.dataset.claspName}.`;
    const colour = this.querySelector('[data-ms-cal="colour"]');
    const colourAt = Number(this.dataset.colourIndex);
    if (colour && colourAt >= 0 && row.o[colourAt]) colour.textContent = `${row.o[colourAt]}.`;

    const img = this.#img;
    if (!img || row.s === this.#shown) return;

    /** @param {number} width */
    const url = (width) => {
      const next = new URL(row.s, location.href);
      next.searchParams.set('width', String(width));
      return next.href;
    };
    const src = url(1200);
    const srcset = WIDTHS.filter((width, i) => i === 0 || width <= row.w)
      .map((width) => `${url(width)} ${width}w`)
      .join(', ');

    const apply = () => {
      this.#shown = row.s;
      img.alt = row.a;
      this.style.setProperty('--ms-cal-ar', String(row.r));
      this.style.setProperty('--ms-cal-fx', String(row.x));
      this.style.setProperty('--ms-cal-fy', String(row.y));
      if (img.hasAttribute('data-ms-defer-src')) {
        // Still waiting for the page's hero image: change what it will load.
        img.dataset.msDeferSrc = src;
        img.dataset.msDeferSrcset = srcset;
      } else {
        img.srcset = srcset;
        img.src = src;
      }
    };

    if (img.hasAttribute('data-ms-defer-src')) return apply();

    // Load and decode first, then swap picture, ratio and print position together so nothing flashes half changed.
    const probe = new Image();
    probe.sizes = img.sizes;
    probe.srcset = srcset;
    probe.src = src;
    probe
      .decode()
      .catch(() => {})
      .then(() => {
        if (token === this.#token) apply();
      });
  }
}

if (!customElements.get('ms-calibre-stage')) customElements.define('ms-calibre-stage', MsCalibreStage);
